import React, { useEffect, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  Outlet,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import './App.css';

import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import Home from './pages/Home';
import Privacy from './pages/Privacy';
import Cookies from './pages/Cookies';
import Terms from './pages/Terms';
import ThankYou from './pages/ThankYou';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Scroll to top on every route change (including language switch handled by NOT re-triggering)
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [pathname]);
  return null;
}

function LanguageLayout() {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && (lang === 'ar' || lang === 'en') && i18n.language !== lang) {
      i18n.changeLanguage(lang);
      // Update document metadata
      document.documentElement.lang = lang === 'ar' ? 'es-AR' : 'en';
    }
  }, [lang, i18n]);

  if (lang !== 'ar' && lang !== 'en') {
    return <Navigate to="/ar" replace />;
  }

  return (
    <div className="site-shell">
      <Header />
      <main className="site-main">
        <Outlet />
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

function ScrollToTopOnPathChange() {
  const location = useLocation();
  const prevPath = React.useRef(location.pathname);

  useEffect(() => {
    // Detect language switch: same page structure but only /:lang segment changed
    const prev = prevPath.current;
    const curr = location.pathname;

    const prevSegments = prev.split('/').filter(Boolean);
    const currSegments = curr.split('/').filter(Boolean);

    const isLangSwitch =
      prevSegments.length === currSegments.length &&
      prevSegments.slice(1).join('/') === currSegments.slice(1).join('/') &&
      prevSegments[0] !== currSegments[0] &&
      (prevSegments[0] === 'ar' || prevSegments[0] === 'en') &&
      (currSegments[0] === 'ar' || currSegments[0] === 'en');

    if (!isLangSwitch) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    prevPath.current = curr;
  }, [location.pathname]);

  return null;
}

function TitleUpdater() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.title = t('meta.title');
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', t('meta.description'));
    document.documentElement.lang = i18n.language === 'ar' ? 'es-AR' : 'en';
  }, [t, i18n.language]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div />}>
        <ScrollToTopOnPathChange />
        <TitleUpdater />
        <Routes>
          <Route path="/" element={<Navigate to="/ar" replace />} />
          <Route path="/thank_you" element={<ThankYou />} />
          <Route path="/:lang" element={<LanguageLayout />}>
            <Route index element={<Home />} />
            <Route path="privacy-policy" element={<Privacy />} />
            <Route path="cookie-policy" element={<Cookies />} />
            <Route path="terms" element={<Terms />} />
          </Route>
          <Route path="*" element={<Navigate to="/ar" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
